import { portalRequest } from '../services/portalRequest'; // v4.28 - G10-B: one request wrapper, carries the chosen company
/**
 * v4.39 - 2026-10-31 - BATCH-2026-10-31 row 40 (option A2): a "Connect your data to see your numbers" banner when /api/me
 *                      says the company skipped Connect your data (connectSkipped) and setup is not done; its one button
 *                      opens /onboarding (Connect your data, OAuth return and Finish Setup). It hides at setup_complete.
 * v4.28 - 2026-10-13 - G10-B (UI-PERMISSION-LIST-NOT-ENFORCED-01): Edit Dashboard is shown only when arrangeOwnDashboard is yes; /api/me and /api/connections go through the one request wrapper (chosen company header)
 * Dashboard Page
 * v4.14 - 2026-07-14 - ITEM-43: DocumentProcessingBanner wired below PipelineFailureBanner.
 * v4.12 - 2026-07-02 - W3-1-F1: 30s banner poll + PipelineFailureBanner top-strip render.
 * v4.11 - 2026-05-05 - WOW-RAG-CONTENT-01d: promote InsightsPanel to position 1 (above data-status row, KPI grid). Insights are the primary surface; supporting tiles secondary.
 * v4.10 - 2026-04-28 - TRUST-KPI-01: tenant-wide DataReliabilityBadge in status row
 * v3.49 - 2026-02-24 - Story 9E: Replace inline Dialog with ChartDialog (ADR-002).
 * v3.40 - 2026-02-24 - KPI card click opens popup chart dialog instead of navigating away (ADR-002).
 * v3.36 - 2026-02-23 - Story 7: "View Charts" navigates to /dashboards (ADR-002).
 * v3.32 - 2026-02-23 - Remove Superset embed (ADR-002: Plotly migration). Drill-down navigates to /reports.
 * v2.96 - 2026-02-07 - Dashboard builder: edit mode with drag-and-drop grid, card picker dialog
 * v2.95 - 2026-02-07 - Added export dropdown (PDF/Excel/CSV) for dashboard KPI data
 * v2.77 - 2026-02-05 - Fixed fetch('/api/me') to include X-Authorization header for Clerk JWT
 * v2.45 - 2026-02-02 - Integrated KPICardGrid with pre-computed KPIs from Cosmos
 * v2.44 - 2026-02-02 - Pipeline orchestration support
 * v2.42 - 2026-02-02 - Company name from tenant_meta
 */
import React, { useEffect, useState, useCallback } from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Box, Container, Typography, Button, Alert, Chip, Menu, MenuItem, ListItemIcon, ListItemText, CircularProgress, Snackbar } from '@mui/material';
import { CloudDone, CloudOff, FileDownload as FileDownloadIcon, PictureAsPdf, TableChart, Description, Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon, Add as AddIcon, BarChart as BarChartIcon } from '@mui/icons-material';
import { exportToPDF, exportToExcel, exportToCSV } from '../services/exportService'; // v2.95
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar';
import KPICardGrid from '../components/KPICardGrid';
import InsightsPanel from '../components/InsightsPanel'; // v3.19
import DashboardGrid from '../components/DashboardGrid'; // v2.96
import CardPickerDialog from '../components/CardPickerDialog'; // v2.96
import { usePermissions } from '../hooks/usePermissions'; // v4.28 - G10-B: screens follow the server's permission map
import ChartDialog from '../components/ChartDialog'; // v3.49 - Story 9E: unified popup
// v4.10 - TRUST-KPI-01: tenant-wide reliability indicator next to Dashboard status chips
import DataReliabilityBadge from '../components/DataReliabilityBadge';
import PipelineFailureBanner from '../components/PipelineFailureBanner'; // v4.12 - W3-1-F1
import DocumentProcessingBanner from '../components/DocumentProcessingBanner'; // v4.14 - ITEM-43
import { dashboardKPIService, bannerService, Banner, ChartDataResponse, ChartReportData, TenantReliability } from '../services/api';
import { useTranslation } from 'react-i18next'; // v4.39 - row 40: banner text
import { onboardingStillOpen } from '../services/firstScreen'; // v4.39 - row 40: the one rule for the banner

// v4.16 - KPI-CARD-CLICK-01: match a KPI card to its report. Primary key is
// relatedKpiCardId; fallback is a category-substring match. Pure + exported so
// matched/unmatched/fallback are unit-testable without a DOM. Returns null when
// nothing matches, so the caller can show a graceful state instead of a dead click.
export function matchReportForCard(
  reports: ChartReportData[] | undefined,
  cardId: string,
): ChartReportData | null {
  if (!reports || !cardId) return null;
  return (
    reports.find((r) => r.relatedKpiCardId === cardId)
    ?? reports.find((r) => !!r.category && cardId.includes(r.category))
    ?? null
  );
}

/** v4.39 - row 40: show the Connect your data banner? Same rule as the /onboarding route (firstScreen.ts). */
export function connectBannerVisible(me: { connectSkipped?: unknown; workflowState?: unknown; captiveComplete?: unknown } | null): boolean {
  if (!me) return false;
  return onboardingStillOpen({
    connectSkipped: me.connectSkipped === true,
    workflowState: typeof me.workflowState === 'string' ? me.workflowState : null,
    captiveComplete: typeof me.captiveComplete === 'string' ? me.captiveComplete : null,
  });
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard'); // v4.39 - row 40
  const [showConnectBanner, setShowConnectBanner] = useState<boolean>(false); // v4.39 - row 40
  const { getToken } = useAuth(); // v2.77 - Clerk token for X-Authorization
  const [tenantId, setTenantId] = useState<string | null>(sessionStorage.getItem('userTenantId'));
  const [loading, setLoading] = useState(!tenantId);
  const [error, setError] = useState<string | null>(null);
  const [connectionsActive, setConnectionsActive] = useState<number>(0);
  // v4.10 - TRUST-KPI-01: tenant reliability summary (null when flag off / pre-trust pipelines)
  const [dataReliability, setDataReliability] = useState<TenantReliability | null>(null);
  // v4.12 - W3-1-F1: degradation banners from /api/banners (polled every 30s per Stage-13 §9)
  const [degradationBanners, setDegradationBanners] = useState<Banner[]>([]);

  // v2.77 - Fetch tenant ID from /api/me with Clerk JWT
  useEffect(() => {
    const fetchTenantId = async () => {
      if (tenantId) return;

      try {
        // v2.77 - Get Clerk token and send via X-Authorization (SWA intercepts standard Authorization)
        const token = await getToken();
        const meResponse = await portalRequest('/api/me', {
          headers: token ? { 'X-Authorization': `Bearer ${token}` } : {},
          credentials: 'include',
        });

        if (meResponse.status === 401) {
          // Not authenticated - redirect will be handled by SWA
          setError('Please log in to continue');
          setLoading(false);
          return;
        }

        const data = await meResponse.json();
        if (meResponse.ok) setShowConnectBanner(connectBannerVisible(data)); // v4.39 - row 40

        if (meResponse.ok && data.tenantId) {
          setTenantId(data.tenantId);
          sessionStorage.setItem('userTenantId', data.tenantId);
          // v2.42 - Store company name in session for use across pages
          if (data.companyName) {
            sessionStorage.setItem('userCompanyName', data.companyName);
          }
          console.log('Loaded tenantId:', data.tenantId);
        } else {
          setError(data.error || data.detail || 'Failed to load user data');
        }
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTenantId();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // v4.39 - BATCH-2026-10-31 row 40: the effect above reads /api/me only when no company key is cached; read the skip
  // note once more when it was cached, so the banner follows the server.
  useEffect(() => {
    if (!sessionStorage.getItem('userTenantId')) return;
    let active = true;
    (async () => {
      try {
        const token = await getToken();
        const r = await portalRequest('/api/me', {
          headers: token ? { 'X-Authorization': `Bearer ${token}` } : {},
          credentials: 'include',
        });
        if (!active || !r.ok) return;
        setShowConnectBanner(connectBannerVisible(await r.json()));
      } catch (err) {
        console.error('Dashboard: connect banner read failed:', err);
      }
    })();
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // v2.45 - Fetch connection count for status display
  useEffect(() => {
    const fetchConnections = async () => {
      if (!tenantId) return;

      try {
        // v2.77 - Get Clerk token for X-Authorization header
        const connToken = await getToken();
        const response = await portalRequest('/api/connections', {
          headers: connToken ? { 'X-Authorization': `Bearer ${connToken}` } : {},
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          const activeCount = (data.connections || []).filter(
            (c: any) => c.status === 'active'
          ).length;
          setConnectionsActive(activeCount);
        }
      } catch (err) {
        console.error('Failed to fetch connections:', err);
      }
    };

    fetchConnections();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // v4.10 - TRUST-KPI-01: fetch KPIs once to surface tenant-wide data_reliability.
  // KPICardGrid does its own fetch; this is intentional to keep the badge state
  // local to Dashboard without lifting all KPI state up.
  useEffect(() => {
    const fetchReliability = async () => {
      if (!tenantId) return;
      try {
        const kpis = await dashboardKPIService.getKPIs();
        setDataReliability(kpis.data_reliability ?? null);
      } catch (err) {
        // Silent failure — badge simply won't render. Don't block the dashboard.
        console.warn('[Dashboard] reliability fetch failed:', err);
      }
    };
    fetchReliability();
  }, [tenantId]);

  // v4.12 - W3-1-F1: poll /api/banners every 30s per Stage-13 locked doc §9.
  // Audience-filtered server-side; renders PipelineFailureBanner at top-strip.
  useEffect(() => {
    if (!tenantId) return;

    const fetchBanners = async () => {
      try {
        const res = await bannerService.getBanners();
        setDegradationBanners(res.banners ?? []);
      } catch (err) {
        // Silent failure per Stage-13 INT-2 — dashboard continues normally.
        console.warn('[Dashboard] banners fetch failed:', err);
      }
    };

    fetchBanners(); // immediate fetch on mount / tenantId ready
    const timer = setInterval(fetchBanners, 30_000); // 30s per §9
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // v3.49 - Story 9E: Unified chart popup via ChartDialog
  const [selectedReport, setSelectedReport] = useState<ChartReportData | null>(null);
  const [chartData, setChartData] = useState<ChartDataResponse | null>(null);
  // v4.16 - KPI-CARD-CLICK-01: info toast shown when a card has no detail view yet.
  const [cardInfoMsg, setCardInfoMsg] = useState<string | null>(null);

  // v3.49 - KPI card click: lazy-fetch chart data, then open ChartDialog with matched report
  const handleCardClick = useCallback(async (cardId: string) => {
    let data = chartData;
    if (!data) {
      try {
        data = await dashboardKPIService.getChartData();
        setChartData(data);
      } catch (err) {
        console.error('[Dashboard] Failed to load chart data:', err);
        setCardInfoMsg('Could not load the detailed view. Please try again.');
        return;
      }
    }
    const matched = matchReportForCard(data?.reports, cardId);
    // v4.16 - KPI-CARD-CLICK-01: an unmatched card must never be a silent no-op.
    // Show a graceful info toast instead of leaving the dialog closed with no feedback.
    if (matched) {
      setSelectedReport(matched);
    } else {
      setCardInfoMsg('No detailed view is available for this card yet.');
    }
  }, [chartData]);

  // v3.36 - View Charts navigates to Dashboards page (Plotly charts via ADR-002)
  const handleViewCharts = () => {
    navigate('/dashboard/insights/dashboards');
  };

  // v3.32 - View Full Report navigates to Reports page
  const handleViewFullReport = () => {
    navigate('/reports');
  };

  // v2.96 - Dashboard builder edit mode state
  // v4.28 - G10-B: arranging the dashboard is allowed only when the server says so for the chosen company
  const { hasPermission } = usePermissions();
  const canArrangeDashboard = hasPermission('arrangeOwnDashboard');
  const [editMode, setEditMode] = useState(false);
  const [cardPickerOpen, setCardPickerOpen] = useState(false);
  const [pendingLayout, setPendingLayout] = useState<Array<{ cardId: string; x: number; y: number; w: number; h: number }>>([]);
  const [gridKey, setGridKey] = useState(0); // Force re-render after card add

  // v4.16 - DASHBOARD-EDIT-VERIFY-01: the real on-dashboard card id list, so the
  // Add-card picker can filter out cards already present (was hardcoded []), and so
  // remove has an authoritative list to refresh. Loaded on mount and after add/remove.
  const [dashboardCardIds, setDashboardCardIds] = useState<string[]>([]);

  const loadDashboardCardIds = useCallback(async () => {
    try {
      const res = await dashboardKPIService.getCards();
      if (res.success) setDashboardCardIds((res.cards || []).map((c) => c.cardId));
    } catch (err) {
      console.error('[Dashboard] Failed to load card ids:', err);
    }
  }, []);

  useEffect(() => {
    if (tenantId) loadDashboardCardIds();
  }, [tenantId, loadDashboardCardIds]);

  // v4.16 - DASHBOARD-EDIT-VERIFY-01: wire the edit-mode remove (X) button. It was
  // rendered by DashboardGrid for removable cards but onRemoveCard was never passed,
  // so the click was a silent no-op. Persist via the existing DELETE endpoint, then
  // refresh the grid and the card-id list.
  const handleRemoveCard = useCallback(async (cardId: string) => {
    try {
      await dashboardKPIService.deleteCard(cardId);
      setGridKey((prev) => prev + 1);
      await loadDashboardCardIds();
    } catch (err) {
      console.error('[Dashboard] Failed to remove card:', err);
      setError('Failed to remove card.');
    }
  }, [loadDashboardCardIds]);

  const handleSaveLayout = async () => {
    try {
      const configRes = await dashboardKPIService.getConfig();
      if (configRes.success) {
        await dashboardKPIService.updateConfig({
          ...configRes.config,
          layout: pendingLayout,
        });
      }
    } catch (err) {
      console.error('Failed to save layout:', err);
      setError('Failed to save dashboard layout.');
    }
    setEditMode(false);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setGridKey((prev) => prev + 1); // Reset grid to saved layout
  };

  // v2.95 - Export dropdown state
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(null);
  const [exporting, setExporting] = useState(false);

  const handleExport = async (format: 'pdf' | 'excel' | 'csv') => {
    setExportAnchorEl(null);
    setExporting(true);
    try {
      if (format === 'pdf') await exportToPDF();
      else if (format === 'excel') await exportToExcel();
      else await exportToCSV();
    } catch (err) {
      console.error(`Export to ${format} failed:`, err);
      setError(`Export failed. Please try again.`);
    } finally {
      setExporting(false);
    }
  };

  // Navigate to Data Sources page
  const handleDataSourcesClick = () => {
    navigate('/dashboard/data');
  };

  // Data status chips
  const renderDataStatus = () => {
    return (
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3, alignItems: 'center' }}>
        <Chip
          icon={connectionsActive > 0 ? <CloudDone /> : <CloudOff />}
          label={`${connectionsActive} Data Source${connectionsActive !== 1 ? 's' : ''}`}
          color={connectionsActive > 0 ? 'success' : 'default'}
          size="small"
        />
        {/* v4.10 - TRUST-KPI-01: tenant reliability badge (renders only when present) */}
        <DataReliabilityBadge reliability={dataReliability} />
      </Box>
    );
  };

  return (
    // v3.30 - Mobile responsive padding - 2026-03-20
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <TopBar title="Dashboard" subtitle="Overview of your business metrics" />
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        {loading && <Alert severity="info" sx={{ mb: 2 }}>Loading user information...</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {tenantId && (
          <>
            {/* v4.39 - BATCH-2026-10-31 row 40: skipped Connect your data -> one banner, one button to /onboarding */}
            {showConnectBanner && (
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                action={
                  <Button color="inherit" size="small" onClick={() => navigate('/onboarding')}>
                    {t('connectBanner.button')}
                  </Button>
                }
              >
                {t('connectBanner.title')}
              </Alert>
            )}
            {/* v4.12 - W3-1-F1: degradation banners — pipeline failure + data-stale (top-strip per Stage-13 §9) */}
            <PipelineFailureBanner banners={degradationBanners} />
            {/* v4.14 - ITEM-43: per-document processing state — polls /api/ingest/document/list */}
            <DocumentProcessingBanner />

            {/* v4.11 - WOW-RAG-CONTENT-01d: AI Insights promoted to position 1 — primary customer-facing surface */}
            <InsightsPanel />

            {renderDataStatus()}

            {/* v2.96 - Edit mode toolbar */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              {editMode ? (
                <>
                  <Chip label="Edit Mode" color="warning" size="small" />
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveLayout}
                    size="small"
                  >
                    Save
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={handleCancelEdit}
                    size="small"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setCardPickerOpen(true)}
                    size="small"
                  >
                    Add Card
                  </Button>
                </>
              ) : (canArrangeDashboard && (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => setEditMode(true)}
                  size="small"
                >
                  Edit Dashboard
                </Button>
              ))}
            </Box>

            {/* v2.96 - Show DashboardGrid in edit mode, KPICardGrid in view mode */}
            {editMode ? (
              <DashboardGrid
                key={gridKey}
                editMode={editMode}
                onCardClick={handleCardClick}
                onRemoveCard={handleRemoveCard}
                onLayoutChange={(layout) => setPendingLayout(layout)}
              />
            ) : (
              <KPICardGrid
                onCardClick={handleCardClick}
                onDataSourcesClick={handleDataSourcesClick}
              />
            )}

            <Box sx={{ mt: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {/* v3.36 - View Charts navigates to Dashboards page (Plotly charts) */}
              <Button
                variant="contained"
                startIcon={<BarChartIcon />}
                onClick={handleViewCharts}
              >
                View Charts
              </Button>
              <Button
                variant="outlined"
                onClick={handleViewFullReport}
              >
                View Reports
              </Button>
              <Button
                variant="outlined"
                sx={{ borderColor: 'primary.main', color: 'primary.main' }}
                onClick={handleDataSourcesClick}
              >
                Manage Data Sources
              </Button>

              {/* v2.95 - Export dropdown */}
              <Button
                variant="outlined"
                startIcon={exporting ? <CircularProgress size={16} /> : <FileDownloadIcon />}
                onClick={(e) => setExportAnchorEl(e.currentTarget)}
                disabled={exporting}
              >
                {exporting ? 'Exporting...' : 'Export'}
              </Button>
              <Menu
                anchorEl={exportAnchorEl}
                open={Boolean(exportAnchorEl)}
                onClose={() => setExportAnchorEl(null)}
              >
                <MenuItem onClick={() => handleExport('pdf')}>
                  <ListItemIcon><PictureAsPdf fontSize="small" /></ListItemIcon>
                  <ListItemText>Export to PDF</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleExport('excel')}>
                  <ListItemIcon><TableChart fontSize="small" /></ListItemIcon>
                  <ListItemText>Export to Excel</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleExport('csv')}>
                  <ListItemIcon><Description fontSize="small" /></ListItemIcon>
                  <ListItemText>Export to CSV</ListItemText>
                </MenuItem>
              </Menu>
            </Box>

            {/* v2.96 - Card picker dialog */}
            <CardPickerDialog
              open={cardPickerOpen}
              onClose={() => setCardPickerOpen(false)}
              onCardsAdded={() => { setGridKey((prev) => prev + 1); loadDashboardCardIds(); }}
              existingCardIds={dashboardCardIds}
            />

            {/* v3.49 - Story 9E: Unified chart popup via ChartDialog */}
            <ChartDialog report={selectedReport} onClose={() => setSelectedReport(null)} />

            {/* v4.16 - KPI-CARD-CLICK-01: graceful feedback when a card has no detail view */}
            <Snackbar
              open={!!cardInfoMsg}
              autoHideDuration={4000}
              onClose={() => setCardInfoMsg(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
              <Alert severity="info" onClose={() => setCardInfoMsg(null)}>
                {cardInfoMsg}
              </Alert>
            </Snackbar>
          </>
        )}
      </Container>

    </Box>
  );
};

export default Dashboard;
